/**
 * Kloutforce Dashboard Layout — thin config wrapper over AppDashboardShell.
 *
 * App-specific responsibilities:
 *  - Nav items configuration
 *  - Sidebar items & SidebarContent (KloutScore, sign-out, branding)
 *  - Logo element
 *  - KatalogBarRow (secondary navbar)
 *  - Right panel slot routing (Discover/Home/Katalog)
 *  - DatasetFacetsProvider wrapper
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  AppDashboardShell,
  AgentCPanelContent,
  type NavbarItemConfig,
  cn,
} from '@bright-web-skeleton/ui';
import { DiscoverRightPanelSlot } from '../discover/desktop/DiscoverRightPanelSlot';
import { HomeRightPanelSlot } from '../home/HomeRightPanelSlot';
import { KatalogRightPanelSlot } from '../katalog-feed/KatalogRightPanelSlot';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '@bright-web-skeleton/auth';
import {
  LayoutDashboard,
  Compass,
  Tag,
  TrendingUp,
  DollarSign,
  Settings,
  LogOut,
  User,
  Bell,
  Home,
  PlusCircle,
  Wallet,
  UserCircle,
  Send,
  Share2,
  Package,
  BarChart3,
  Crown,
  Shield,
  ShieldCheck,
  Handshake,
  BookOpen,
  Users,
} from 'lucide-react';
import { KloutScoreBadge } from '../profile';
import { KatalogBarRow } from './KatalogBarRow';
import { DatasetFacetsProvider } from '../../lib/DatasetFacetsContext';
import { useProgramDashboards } from '../../hooks/useProgramDashboards';
import { PartnerToAgentUpgradeButton } from '../program/PartnerToAgentUpgradeButton';

// ─── Nav items ───────────────────────────────────────────────────
const navbarItems: NavbarItemConfig[] = [
  { label: 'Home', path: '/home', icon: Home },
  { label: 'Discover', path: '/discover', icon: Compass },
  { label: 'Create', path: '/create', icon: PlusCircle },
  { label: 'Notifications', path: '/notifications', icon: Bell },
  { label: 'Account', icon: User, action: 'toggle-sidebar' },
];

// ─── Sidebar items ───────────────────────────────────────────────
type SidebarItemType = 'item' | 'headline' | 'cta';

interface SidebarEntry {
  type: SidebarItemType;
  label: string;
  route?: string;
  icon?: React.ComponentType<{ className?: string }>;
  style?: 'primary';
}

const sidebarItems: SidebarEntry[] = [
  { type: 'item', label: 'Dashboard', route: '/dashboard', icon: LayoutDashboard },

  { type: 'headline', label: 'YOUR KATALOG' },
  { type: 'item', label: 'Katalags', route: '/account/katalogs', icon: Package },
  { type: 'item', label: 'Brand Deals', route: '/account/deals', icon: Tag },
  { type: 'item', label: 'Performance', route: '/account/performance', icon: BarChart3 },

  { type: 'headline', label: 'ACCOUNT' },
  { type: 'item', label: 'Profile', route: '/account/profile', icon: UserCircle },
  { type: 'item', label: 'Connect Socials', route: '/account/socials', icon: Share2 },
  { type: 'item', label: 'Earnings', route: '/account/earnings', icon: DollarSign },
  { type: 'item', label: 'Wallet', route: '/account/wallet', icon: Wallet },
  { type: 'item', label: 'Invites', route: '/account/invites', icon: Users },
  { type: 'item', label: 'Roles', route: '/account/professional-roles', icon: ShieldCheck },
  { type: 'item', label: 'Instructions', route: '/instructions', icon: BookOpen },
  { type: 'item', label: 'Settings', route: '/account/settings', icon: Settings },

  { type: 'cta', label: 'Invite', route: '/invite', icon: Send, style: 'primary' },
];

// ─── Component ───────────────────────────────────────────────────

interface DashboardLayoutProps {
  children?: React.ReactNode;
}

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const { appProfile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { dashboards: programDashboards } = useProgramDashboards();
  const [agentCAccessToken, setAgentCAccessToken] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    void supabase.auth.getSession().then(({ data }) => {
      if (mounted) {
        setAgentCAccessToken(data.session?.access_token ?? null);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (mounted) {
        setAgentCAccessToken(session?.access_token ?? null);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // ─── KatalogBar visibility ──────────────────────────────────
  const isKatalogRoute = location.pathname === '/account/katalog';
  const [showKatalogBar, setShowKatalogBar] = useState(false);

  useEffect(() => {
    if (!isKatalogRoute) {
      setShowKatalogBar(false);
      return;
    }
    setShowKatalogBar(document.documentElement.dataset.katalogBar === '1');
    const observer = new MutationObserver(() => {
      setShowKatalogBar(document.documentElement.dataset.katalogBar === '1');
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-katalog-bar'],
    });
    return () => observer.disconnect();
  }, [isKatalogRoute]);

  // ─── Helpers ────────────────────────────────────────────────
  const isPathActive = useCallback(
    (path: string) => location.pathname === path || location.pathname.startsWith(path + '/'),
    [location.pathname],
  );

  const handleSidebarLinkClick = useCallback(
    (path: string) => {
      navigate(path);
    },
    [navigate],
  );

  const handleSignOut = useCallback(async () => {
    await supabase.auth.signOut();
    navigate('/auth');
  }, [navigate]);

  // ─── Route detection for panel slots ────────────────────────
  const isDiscoverRoute = location.pathname === '/discover' || location.pathname.startsWith('/discover/');
  const isHomeRoute = location.pathname === '/home' || location.pathname.startsWith('/home/');
  const isKatalogFeedRoute =
    location.pathname === '/katalog' ||
    location.pathname.startsWith('/katalog/') ||
    location.pathname.startsWith('/account/katalog');

  // ─── Profile ────────────────────────────────────────────────
  const memberProfile = appProfile as {
    display_name?: string;
    username?: string;
    avatar_url?: string;
    kloutscore?: number | null;
    kloutscore_verified_at?: string | null;
    kloutscore_source?: string | null;
  } | null;

  // ─── App-specific: Logo ─────────────────────────────────────
  const logo = (
    <div className="flex items-center gap-3">
      <Link
        to="/dashboard"
        className="flex items-center gap-2 no-underline"
      >
        <img src="/brand/kloutforce-logo.png" alt="Kloutforce" className="h-8 w-8 rounded-lg object-contain" />
        <span className="text-lg font-semibold text-foreground hidden sm:inline">
          Kloutforce
        </span>
      </Link>
    </div>
  );

  // ─── App-specific: Secondary navbar (KatalogBar) ────────────
  const secondaryNavbar = isKatalogRoute && showKatalogBar ? (
    <div className="h-14 border-t border-border relative">
      <KatalogBarRow
        name={memberProfile?.display_name ?? memberProfile?.username ?? 'Your Katalag'}
        avatarUrl={memberProfile?.avatar_url}
      />
    </div>
  ) : undefined;

  // ─── App-specific: Sidebar content ─────────────────────────
  const accountContent = (
    <>
      {/* Profile header + KloutScore */}
      <div className="h-auto flex flex-col gap-2 px-4 py-3 border-b border-border">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-foreground truncate">
            {memberProfile?.display_name || memberProfile?.username || 'Creator'}
          </span>
        </div>
        {memberProfile && (
          <KloutScoreBadge
            kloutscore={memberProfile.kloutscore ?? null}
            verifiedAt={memberProfile.kloutscore_verified_at ?? null}
            source={memberProfile.kloutscore_source ?? null}
            size="sm"
            showPending
          />
        )}
      </div>

      {/* Navigation */}
      <nav className="py-4 px-3" aria-label="Sidebar navigation">
        <ul className="space-y-1" role="list">
          {sidebarItems.map((entry, index) => {
            if (entry.type === 'headline') {
              return (
                <li key={`headline-${index}`} className="pt-4 first:pt-0">
                  <h3 className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground select-none" aria-hidden="true" role="presentation">
                    {entry.label}
                  </h3>
                </li>
              );
            }

            if (entry.type === 'cta') {
              const isActive = entry.route ? isPathActive(entry.route) : false;
              const Icon = entry.icon;
              return (
                <li key={entry.route || `cta-${index}`} className="pt-4">
                  <button
                    onClick={() => entry.route && handleSidebarLinkClick(entry.route)}
                    className={cn(
                      'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left',
                      isActive
                        ? 'bg-primary/10 text-primary'
                        : 'bg-gradient-to-r from-pink-500 to-purple-500 text-white hover:from-pink-600 hover:to-purple-600',
                    )}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    {Icon && <Icon className="w-5 h-5 shrink-0" />}
                    {entry.label}
                  </button>
                </li>
              );
            }

            const Icon = entry.icon;
            const isActive = entry.route ? isPathActive(entry.route) : false;
            return (
              <li key={entry.route || `item-${index}`}>
                <button
                  onClick={() => entry.route && handleSidebarLinkClick(entry.route)}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left',
                    isActive
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted',
                  )}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {Icon && <Icon className="w-5 h-5 shrink-0" />}
                  {entry.label}
                </button>
              </li>
            );
          })}
          {/* Dynamic program dashboard links */}
          {(programDashboards.length > 0 || !programDashboards.some(pd => pd.programType === 'partner')) && (
            <>
              <li className="pt-4">
                <h3 className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground select-none" aria-hidden="true" role="presentation">
                  PROFESSIONAL ROLES
                </h3>
              </li>
              {programDashboards.map((pd) => {
                const isActive = isPathActive(pd.route);
                const PdIcon = pd.icon === 'link' ? Share2 : pd.icon === 'crown' ? Crown : pd.icon === 'shield' ? Shield : Handshake;
                return (
                  <li key={pd.route}>
                    <button
                      onClick={() => handleSidebarLinkClick(pd.route)}
                      className={cn(
                        'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left',
                        isActive
                          ? 'bg-primary/10 text-primary'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted',
                      )}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      <PdIcon className="w-5 h-5 shrink-0" />
                      {pd.label}
                    </button>
                  </li>
                );
              })}
              {/* Upsell: Become a Partner — hidden for Agents (Partner is a
                  separate role lane; Agents can invite Partners but cannot
                  convert into Partner). Also hidden if user already has Partner
                  or General Agent role to avoid noise. */}
              {!programDashboards.some(pd =>
                pd.programType === 'partner' ||
                pd.programType === 'agent' ||
                pd.programType === 'general_agent' ||
                pd.programType === 'connector',
              ) && (
                <li>
                  <button
                    onClick={() => handleSidebarLinkClick('/partner/upgrade')}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left text-muted-foreground hover:text-foreground hover:bg-muted"
                  >
                    <Handshake className="w-5 h-5 shrink-0" />
                    Become a Partner
                  </button>
                </li>
              )}
              {/* Upsell: Become an Agent if user has partner but not agent */}
              {programDashboards.some(pd => pd.programType === 'partner') && !programDashboards.some(pd => pd.programType === 'agent') && (
                <li>
                  <PartnerToAgentUpgradeButton>
                    {(open) => (
                      <button
                        onClick={open}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left text-muted-foreground hover:text-foreground hover:bg-muted"
                      >
                        <Shield className="w-5 h-5 shrink-0" />
                        Upgrade Partner to Agent
                      </button>
                    )}
                  </PartnerToAgentUpgradeButton>
                </li>
              )}
            </>
          )}
        </ul>
      </nav>
      <div className="py-4 px-3 border-t border-border mt-auto">
        <button
          onClick={handleSignOut}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors"
          aria-label="Sign out of your account"
        >
          <LogOut className="w-5 h-5 shrink-0" />
          Sign Out
        </button>
      </div>

      {/* Branding */}
      <div className="p-3 border-t border-border">
        <div className="w-full text-center p-2">
          <p className="text-sm font-medium bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
            Kloutforce
          </p>
          <p className="text-xs text-muted-foreground">info@kloutforce.com</p>
        </div>
      </div>
    </>
  );

  // ─── App-specific: Panel slot content ───────────────────────
  const filtersContent = (
    <>
      {isDiscoverRoute && <DiscoverRightPanelSlot panel="filters" />}
      {isHomeRoute && <HomeRightPanelSlot panel="filters" />}
      {isKatalogFeedRoute && <KatalogRightPanelSlot panel="filters" />}
    </>
  );

  const searchContent = (
    <>
      {isDiscoverRoute && <DiscoverRightPanelSlot panel="search" />}
      {isHomeRoute && <HomeRightPanelSlot panel="search" />}
      {isKatalogFeedRoute && <KatalogRightPanelSlot panel="search" />}
    </>
  );

  const agentCContent = (
    <AgentCPanelContent
      role="creator"
      supabaseUrl={import.meta.env.VITE_SUPABASE_URL ?? ''}
      accessToken={agentCAccessToken}
      context={
        isDiscoverRoute
          ? { type: 'discover', label: 'Discover' }
          : { type: 'general', label: 'Kloutforce' }
      }
      suggestedPrompts={[
        'Which deals should I consider for my Katalag?',
        'Help me compare the opportunities available to me.',
        'What should I focus on next as a creator?',
      ]}
    />
  );

  // ─── Header offset ─────────────────────────────────────────
  const headerOffset = isKatalogRoute && showKatalogBar ? '112px' : '56px';

  return (
    <AppDashboardShell
      logo={logo}
      navItems={navbarItems}
      onNavigate={navigate}
      isPathActive={isPathActive}
      secondaryNavbar={secondaryNavbar}
      accountContent={accountContent}
      filtersContent={filtersContent}
      searchContent={searchContent}
      agentCContent={agentCContent}
      // Phase 4 visual consistency: canonical Kloutforce surface applied to ALL
      // dashboard routes (Home, Discover, Wallet, Earnings, Account, network
      // dashboards, etc.) so internal pages no longer render on plain white.
      // Individual pages that still wrap themselves in `bg-background` should
      // be migrated to soft cards over this surface in follow-up passes.
      mainClassName="bg-katalog-surface"
      headerOffset={headerOffset}
    >
      {children || <Outlet />}
    </AppDashboardShell>
  );
};

const DashboardLayoutWithFacets: React.FC<React.ComponentProps<typeof DashboardLayout>> = (props) => (
  <DatasetFacetsProvider>
    <DashboardLayout {...props} />
  </DatasetFacetsProvider>
);

export default DashboardLayoutWithFacets;
